import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, message, Tag, Space, Tabs, Typography, Row, Col, Tooltip, Radio, Alert } from 'antd';
import { PlusOutlined, SearchOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, EyeOutlined, ThunderboltOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { linkRequestsAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAuthStore } from '../store/authStore';

const { Text } = Typography;

interface Supplier {
  id: number;
  companyName: string;
  inn: string;
  contactName: string;
  contactPhone: string;
  email: string;
  address?: string;
  description?: string;
  isApproved: boolean;
  isActive: boolean;
  rating?: number;
  contactPosition?: string | null;
  contactEmail?: string | null;
  user?: {
    id: number;
    username?: string;
    fullName: string;
    email: string;
    phone: string;
    companyName: string;
    inn: string;
    approval?: {
      responsibleFullName?: string | null;
      responsiblePhone?: string | null;
      responsibleEmail?: string | null;
      responsiblePosition?: string | null;
    } | null;
  };
}

interface LinkRequest {
  id: number;
  customerId: number;
  supplierId: number;
  status: string;
  createdAt: string;
  supplier?: Supplier; // Может быть undefined
  initiatorType: string; // "customer" или "supplier"
  message?: string;
  discountPercent?: number;
  allowEmergencyOrders?: boolean;
  emergencyEnabledAt?: string | null;
  emergencyExpiresAt?: string | null;
}

const MySuppliers: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useAuthStore();
  const isCustomer = user?.role === 'customer';
  const [loading, setLoading] = useState(false);
  const [myRequests, setMyRequests] = useState<LinkRequest[]>([]);
  const [searchModalVisible, setSearchModalVisible] = useState(false);
  const [searchResults, setSearchResults] = useState<Supplier[]>([]);
  const [searching, setSearching] = useState(false);
  const [tab, setTab] = useState<'approved' | 'pending' | 'rejected'>('approved');
  const [form] = Form.useForm();
  const [emergencyToggleId, setEmergencyToggleId] = useState<number | null>(null);
  const [orderingMenu, setOrderingMenu] = useState<{
    orderingSupplierId: number | null;
    suppliers: Array<{ id: number; name: string }>;
    canSwitch: boolean;
  } | null>(null);
  const [switchingMenu, setSwitchingMenu] = useState(false);

  useEffect(() => {
    loadMyRequests();
    loadOrderingMenu();
  }, []);

  const loadOrderingMenu = async () => {
    try {
      const data = await linkRequestsAPI.getOrderingMenu();
      setOrderingMenu(data);
    } catch {
      setOrderingMenu(null);
    }
  };

  const loadMyRequests = async () => {
    setLoading(true);
    try {
      const data = await linkRequestsAPI.getCustomerRequests();
      console.log('📊 Loaded requests data:', data);
      if (data.length > 0) {
        console.log('📊 First request supplier structure:', data[0].supplier);
      }
      setMyRequests(data);
    } catch (error: any) {
      message.error(error?.response?.data?.error || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (values: { inn?: string; companyName?: string }) => {
    setSearching(true);
    try {
      console.log('🔍 Searching suppliers with values:', values);
      const data = await linkRequestsAPI.searchSuppliers(values);
      console.log('📊 Search results:', data);
      if (data.length > 0) {
        console.log('📊 First search result structure:', data[0]);
      }
      setSearchResults(data);
      
      if (data.length === 0) {
        message.info('Поставщики не найдены');
      } else {
        message.success(`Найдено поставщиков: ${data.length}`);
      }
    } catch (error: any) {
      console.error('❌ Search error:', error);
      message.error(error?.response?.data?.error || 'Ошибка поиска');
    } finally {
      setSearching(false);
    }
  };

  const handleSendRequest = async (supplierId: number) => {
    try {
      const values = form.getFieldsValue();
      await linkRequestsAPI.createRequest({ supplierId, message: values.message });
      message.success('Заявка отправлена поставщику');
      setSearchModalVisible(false);
      form.resetFields();
      setSearchResults([]);
      loadMyRequests();
    } catch (error: any) {
      message.error(error?.response?.data?.error || 'Ошибка отправки заявки');
    }
  };

  const handleSetOrderingMenu = async (supplierId: number, supplierName: string) => {
    if (orderingMenu?.orderingSupplierId === supplierId) return;
    let pending = { willAnnul: false, employeeCount: 0, itemCount: 0 };
    try {
      pending = await linkRequestsAPI.getOrderingMenuPendingCarts(supplierId);
    } catch {
      message.error('Не удалось проверить корзины сотрудников');
      return;
    }

    Modal.confirm({
      title: 'Сменить меню для заказов?',
      content: (
        <div>
          <p>
            Сотрудники будут видеть и заказывать только меню поставщика{' '}
            <strong>{supplierName}</strong>.
          </p>
          {pending.willAnnul ? (
            <p style={{ color: '#cf1322', marginBottom: 0 }}>
              <strong>Внимание:</strong> в корзинах другого поставщика осталось{' '}
              {pending.itemCount} поз. у {pending.employeeCount}{' '}
              {pending.employeeCount === 1 ? 'сотрудника' : 'сотрудников'}. При переключении эти
              корзины будут <strong>аннулированы</strong> — сотрудникам придёт уведомление.
            </p>
          ) : (
            <p style={{ marginBottom: 0 }}>Незавершённых корзин у других поставщиков нет.</p>
          )}
        </div>
      ),
      okText: pending.willAnnul ? 'Аннулировать и переключить' : 'Переключить',
      cancelText: 'Отмена',
      okButtonProps: pending.willAnnul ? { danger: true } : undefined,
      onOk: async () => {
        setSwitchingMenu(true);
        try {
          const res = await linkRequestsAPI.setOrderingMenu(supplierId);
          setOrderingMenu((prev) =>
            prev ? { ...prev, orderingSupplierId: res.orderingSupplierId } : prev
          );
          if (res.annulled?.itemCount > 0) {
            message.warning(
              `Меню переключено на ${res.supplierName}. Аннулировано ${res.annulled.itemCount} поз. у ${res.annulled.employeeCount} сотрудников — им отправлено уведомление.`
            );
          } else {
            message.success(`Для заказов выбрано меню: ${res.supplierName}`);
          }
        } catch (error: any) {
          message.error(error?.response?.data?.error || 'Не удалось переключить меню');
        } finally {
          setSwitchingMenu(false);
        }
      },
    });
  };

  const handleApproveRequest = async (requestId: number) => {
    // Находим заявку для отображения деталей
    const request = myRequests.find(r => r.id === requestId);
    if (!request?.supplier) {
      message.error('Информация о поставщике недоступна');
      return;
    }
    
    const supplierData = getSupplierData(request.supplier);
    const supplierName = supplierData.companyName;
    const supplierContact = supplierData.fullName;
    
    Modal.confirm({
      title: 'Подтверждение одобрения заявки',
      content: (
        <div>
          <p><strong>⚠️ Внимание!</strong> Вы собираетесь одобрить заявку на сотрудничество.</p>
          <div style={{ 
            background: '#f6ffed', 
            border: '1px solid #b7eb8f', 
            borderRadius: '6px', 
            padding: '12px', 
            margin: '12px 0' 
          }}>
            <p><strong>Поставщик:</strong> {supplierName}</p>
            <p><strong>Контакт:</strong> {supplierContact}</p>
            {request?.message && <p><strong>Сообщение:</strong> {request.message}</p>}
          </div>
          <p>После одобрения поставщик получит email уведомление и сможет видеть ваши заказы.</p>
          <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
            Это действие нельзя отменить!
          </p>
        </div>
      ),
      okText: 'Одобрить заявку',
      cancelText: 'Отмена',
      okType: 'primary',
      width: 500,
      onOk: async () => {
        try {
          await linkRequestsAPI.approveRequest(requestId);
          message.success('✅ Заявка одобрена! Поставщик получил email уведомление.');
          loadMyRequests();
        } catch (error: any) {
          message.error(error?.response?.data?.error || 'Ошибка одобрения заявки');
        }
      }
    });
  };

  const handleRejectRequest = async (requestId: number) => {
    // Находим заявку для отображения деталей
    const request = myRequests.find(r => r.id === requestId);
    if (!request?.supplier) {
      message.error('Информация о поставщике недоступна');
      return;
    }
    
    const supplierData = getSupplierData(request.supplier);
    const supplierName = supplierData.companyName;
    const supplierContact = supplierData.fullName;
    
    Modal.confirm({
      title: 'Подтверждение отклонения заявки',
      content: (
        <div>
          <p><strong>⚠️ Внимание!</strong> Вы собираетесь отклонить заявку на сотрудничество.</p>
          <div style={{ 
            background: '#fff2f0', 
            border: '1px solid #ffccc7', 
            borderRadius: '6px', 
            padding: '12px', 
            margin: '12px 0' 
          }}>
            <p><strong>Поставщик:</strong> {supplierName}</p>
            <p><strong>Контакт:</strong> {supplierContact}</p>
            {request?.message && <p><strong>Сообщение:</strong> {request.message}</p>}
          </div>
          <p>После отклонения поставщик получит email уведомление с объяснением причины.</p>
          <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
            Это действие нельзя отменить!
          </p>
        </div>
      ),
      okText: 'Отклонить заявку',
      cancelText: 'Отмена',
      okType: 'danger',
      width: 500,
      onOk: async () => {
        try {
          await linkRequestsAPI.rejectRequest(requestId);
          message.success('❌ Заявка отклонена! Поставщик получил email уведомление.');
          loadMyRequests();
        } catch (error: any) {
          message.error(error?.response?.data?.error || 'Ошибка отклонения заявки');
        }
      }
    });
  };

  const handleEmergencyToggle = async (request: LinkRequest, enabled: boolean) => {
    try {
      setEmergencyToggleId(request.id);
      await linkRequestsAPI.toggleEmergencyOrder(request.id, enabled);
      message.success(enabled ? 'Внеплановый заказ включен' : 'Внеплановый заказ выключен');
      loadMyRequests();
    } catch (error: any) {
      message.error(error?.response?.data?.error || 'Не удалось изменить режим');
    } finally {
      setEmergencyToggleId(null);
    }
  };

  const getStatusTag = (status: string) => {
    const configs: { [key: string]: { color: string; icon: React.ReactNode; text: string } } = {
      pending: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Ожидает' },
      approved: { color: 'green', icon: <CheckCircleOutlined />, text: 'Одобрена' },
      rejected: { color: 'red', icon: <CloseCircleOutlined />, text: 'Отклонена' }
    };
    const config = configs[status] || configs.pending;
    
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  // Вспомогательная функция для безопасного получения данных поставщика
  const getSupplierData = (supplier: Supplier | undefined) => {
    if (!supplier) {
      return {
        companyName: 'Не указано',
        inn: 'Не указан',
        fullName: 'Не указано',
        phone: 'Не указан',
        email: 'Не указан'
      };
    }
    
    const userData = supplier.user;
      const approval = userData?.approval;
      return {
        companyName: userData?.companyName || supplier.companyName || 'Не указано',
        inn: userData?.inn || supplier.inn || 'Не указан',
        /** Актуальные поля профиля поставщика важнее зафиксированных при регистрации */
        fullName:
          userData?.fullName ||
          approval?.responsibleFullName ||
          supplier.contactName ||
          userData?.username ||
          'Не указано',
        phone:
          userData?.phone ||
          approval?.responsiblePhone ||
          supplier.contactPhone ||
          'Не указан',
        email: userData?.email || approval?.responsibleEmail || supplier.email || 'Не указан',
        position: approval?.responsiblePosition || supplier.contactPosition || '—'
      };
  };

  const handleViewSupplier = (supplier: Supplier | undefined) => {
    if (!supplier) {
      message.error('Информация о поставщике недоступна');
      return;
    }
    
    const data = getSupplierData(supplier);
    
    Modal.info({
      title: 'Информация о поставщике',
      content: (
        <div>
          <p><strong>Компания:</strong> {data.companyName}</p>
          <p><strong>ИНН:</strong> {data.inn}</p>
          <p><strong>Контактное лицо:</strong> {data.fullName}</p>
          <p><strong>Телефон:</strong> {data.phone}</p>
          <p><strong>Email:</strong> {data.email}</p>
          <p><strong>Статус:</strong> {supplier.isApproved ? 'Одобрен' : 'Ожидает одобрения'}</p>
          <p><strong>Активность:</strong> {supplier.isActive ? 'Активен' : 'Неактивен'}</p>
          {supplier.rating && <p><strong>Рейтинг:</strong> {supplier.rating}/5</p>}
        </div>
      ),
      width: 600,
    });
  };

  const columns = [
    {
      title: 'Название компании',
      key: 'companyName',
      width: 200,
      ellipsis: true,
      render: (text: string, record: LinkRequest) => {
        const data = getSupplierData(record.supplier);
        return (
          <Button 
            type="link" 
            onClick={() => handleViewSupplier(record.supplier)}
            style={{ padding: 0, height: 'auto', fontWeight: 500 }}
          >
            {data.companyName}
          </Button>
        );
      },
    },
    {
      title: 'ИНН',
      key: 'inn',
      width: 120,
      render: (text: string, record: LinkRequest) => {
        const data = getSupplierData(record.supplier);
        return <Text code>{data.inn}</Text>;
      },
    },
    {
      title: 'Контакт',
      key: 'contact',
      width: 180,
      ellipsis: true,
      render: (record: LinkRequest) => {
        const data = getSupplierData(record.supplier);
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{data.fullName}</div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
              {data.position}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {data.phone}
            </Text>
          </div>
        );
      },
    },
    {
      title: 'Email',
      key: 'email',
      width: 200,
      ellipsis: true,
      render: (record: LinkRequest) => {
        const data = getSupplierData(record.supplier);
        return <Text type="secondary">{data.email}</Text>;
      },
    },
    {
      title: 'Внеплановый заказ',
      key: 'emergency',
      width: 220,
      render: (record: LinkRequest) => {
        if (!record.allowEmergencyOrders) {
          return <Tag color="default">Не разрешено поставщиком</Tag>;
        }
        const expiresAt = record.emergencyExpiresAt ? dayjs(record.emergencyExpiresAt) : null;
        const emergencyActive =
          !!record.emergencyEnabledAt &&
          (!expiresAt || expiresAt.isAfter(dayjs()));

        return (
          <Space direction="vertical" size={4}>
            <Tag color={emergencyActive ? 'red' : 'green'}>
              {emergencyActive
                ? `Активен до ${expiresAt ? expiresAt.format('DD.MM HH:mm') : 'отмены'}`
                : 'Доступен'}
            </Tag>
            <Button
              size="small"
              icon={<ThunderboltOutlined />}
              loading={emergencyToggleId === record.id}
              disabled={record.status !== 'approved'}
              onClick={() => handleEmergencyToggle(record, !emergencyActive)}
            >
              {emergencyActive ? 'Выключить' : 'Включить'}
            </Button>
          </Space>
        );
      }
    },
    {
      title: 'Инициатор',
      key: 'initiator',
      width: 140,
      render: (record: LinkRequest) => (
        <Tag color={record.initiatorType === 'customer' ? 'blue' : 'green'}>
          {record.initiatorType === 'customer' ? 'Вы отправили' : 'Поставщик предложил'}
        </Tag>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: 'Дата заявки',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString('ru-RU'),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 200,
      render: (record: LinkRequest) => {
        // В табе "Ожидают" показываем кнопки только для входящих заявок (от поставщика)
        if (tab === 'pending' && record.initiatorType === 'supplier' && record.status === 'pending') {
          return (
            <Space>
              <Button 
                type="primary" 
                size="small" 
                icon={<CheckCircleOutlined />}
                onClick={() => handleApproveRequest(record.id)}
              >
                Одобрить
              </Button>
              <Button 
                danger 
                size="small" 
                icon={<CloseCircleOutlined />}
                onClick={() => handleRejectRequest(record.id)}
              >
                Отклонить
              </Button>
            </Space>
          );
        }
        
        // Для исходящих заявок (которые заказчик отправил) показываем статус ожидания
        if (tab === 'pending' && record.initiatorType === 'customer' && record.status === 'pending') {
          return (
            <Tag color="orange" icon={<ClockCircleOutlined />}>
              Ожидаем ответа
            </Tag>
          );
        }

        // Для одобренных заявок показываем кнопку перехода к меню
        if (record.status === 'approved') {
          const isOrdering = orderingMenu?.orderingSupplierId === record.supplierId;
          return (
            <Space>
              {isOrdering ? (
                <Tag color="green">Меню для заказов</Tag>
              ) : (
                <Button
                  size="small"
                  loading={switchingMenu}
                  onClick={() => handleSetOrderingMenu(record.supplierId, getSupplierData(record.supplier).companyName)}
                >
                  Использовать для заказов
                </Button>
              )}
              <Button
                type="primary"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/supplier-menu/${record.supplier.id}`)}
              >
                Меню
              </Button>
            </Space>
          );
        }
        
        return null;
      },
    },
  ];

  // Сначала группируем все заявки по supplierId и выбираем актуальный статус
  const getCurrentStatusForSuppliers = () => {
    const supplierStatusMap = new Map();
    
    myRequests.forEach(request => {
      const supplierId = request.supplierId;
      const currentStatus = supplierStatusMap.get(supplierId);
      
      // Приоритет статусов: approved > pending > rejected
      if (!currentStatus || 
          (currentStatus.status !== 'approved' && request.status === 'approved') ||
          (currentStatus.status === 'rejected' && request.status !== 'rejected')) {
        supplierStatusMap.set(supplierId, request);
      }
    });
    
    return supplierStatusMap;
  };

  // Получаем заявки для текущего таба, учитывая актуальный статус каждого поставщика
  const filteredRequests = (() => {
    const supplierStatusMap = getCurrentStatusForSuppliers();
    const requests = Array.from(supplierStatusMap.values());
    
    return requests.filter(r => {
      if (tab === 'approved') {
        return r.status === 'approved';
      } else if (tab === 'pending') {
        return r.status === 'pending';
      } else if (tab === 'rejected') {
        return r.status === 'rejected';
      }
      return false;
    });
  })();

  return (
    <>
      <Card
        title="Мои поставщики"
        extra={
          isCustomer ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setSearchModalVisible(true)}
            >
              Найти поставщика
            </Button>
          ) : undefined
        }
      >
        {orderingMenu && orderingMenu.suppliers.length > 1 && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Меню для заказов сотрудников"
            description={
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Text>
                  Одновременно сотрудникам доступно меню только одного поставщика. Сейчас:{' '}
                  <Text strong>
                    {orderingMenu.suppliers.find((s) => s.id === orderingMenu.orderingSupplierId)?.name || 'не выбран'}
                  </Text>
                </Text>
                <Radio.Group
                  value={orderingMenu.orderingSupplierId ?? undefined}
                  onChange={(e) => {
                    const id = e.target.value as number;
                    const name = orderingMenu.suppliers.find((s) => s.id === id)?.name || '';
                    handleSetOrderingMenu(id, name);
                  }}
                  disabled={switchingMenu}
                  optionType="button"
                  buttonStyle="solid"
                >
                  {orderingMenu.suppliers.map((s) => (
                    <Radio.Button key={s.id} value={s.id}>
                      {s.name}
                    </Radio.Button>
                  ))}
                </Radio.Group>
              </Space>
            }
          />
        )}
        <Tabs
          activeKey={tab}
          onChange={(key) => setTab(key as any)}
          items={[
            {
              key: 'approved',
              label: `Активные (${(() => {
                const supplierStatusMap = getCurrentStatusForSuppliers();
                return Array.from(supplierStatusMap.values()).filter(r => r.status === 'approved').length;
              })()})`,
              children: (
                <Table
                  columns={columns}
                  dataSource={filteredRequests}
                  loading={loading}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  scroll={isMobile ? { x: 'max-content' } : undefined}
                  size={isMobile ? 'small' : 'middle'}
                />
              )
            },
            {
              key: 'pending',
              label: `Ожидают (${(() => {
                const supplierStatusMap = getCurrentStatusForSuppliers();
                return Array.from(supplierStatusMap.values()).filter(r => r.status === 'pending').length;
              })()})`,
              children: (
                <Table
                  columns={columns}
                  dataSource={filteredRequests}
                  loading={loading}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  scroll={isMobile ? { x: 'max-content' } : undefined}
                  size={isMobile ? 'small' : 'middle'}
                />
              )
            },
            {
              key: 'rejected',
              label: `Отклоненные (${(() => {
                const supplierStatusMap = getCurrentStatusForSuppliers();
                return Array.from(supplierStatusMap.values()).filter(r => r.status === 'rejected').length;
              })()})`,
              children: (
                <Table
                  columns={columns}
                  dataSource={filteredRequests}
                  loading={loading}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  scroll={isMobile ? { x: 'max-content' } : undefined}
                  size={isMobile ? 'small' : 'middle'}
                />
              )
            }
          ]}
        />
      </Card>

      <Modal
        title="Найти поставщика"
        open={searchModalVisible}
        onCancel={() => {
          setSearchModalVisible(false);
          form.resetFields();
          setSearchResults([]);
        }}
        footer={null}
        width={isMobile ? '100%' : 900}
        centered={!isMobile}
        style={isMobile ? { top: 0, paddingBottom: 0 } : { top: 20 }}
      >
        <div style={{ maxHeight: '80vh', overflow: 'auto' }}>
          {/* Форма поиска */}
          <Card 
            size="small" 
            title="Критерии поиска" 
            style={{ marginBottom: 16 }}
            bodyStyle={{ padding: '16px' }}
          >
            <Form 
              form={form} 
              onFinish={handleSearch} 
              layout="vertical"
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="inn"
                    label="ИНН поставщика"
                  >
                    <Input placeholder="10 или 12 цифр" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="companyName"
                    label="Название компании"
                  >
                    <Input placeholder="Введите название компании" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  icon={<SearchOutlined />} 
                  loading={searching}
                  size="large"
                >
                  Найти поставщиков
                </Button>
              </Form.Item>
            </Form>
          </Card>

          {/* Форма сообщения */}
          <Card 
            size="small" 
            title="Сообщение поставщику" 
            style={{ marginBottom: 16 }}
            bodyStyle={{ padding: '16px' }}
          >
            <Form layout="vertical">
              <Form.Item name="message" label="Текст сообщения (необязательно)">
                <Input.TextArea 
                  rows={3} 
                  placeholder="Краткое сообщение или условия сотрудничества"
                  maxLength={500}
                  showCount
                />
              </Form.Item>
            </Form>
          </Card>

          {/* Результаты поиска */}
          {searchResults.length > 0 && (
            <Card 
              size="small" 
              title={`Найдено поставщиков: ${searchResults.length}`}
              bodyStyle={{ padding: '16px' }}
            >
              <Table
                dataSource={searchResults}
                rowKey="id"
                pagination={false}
                scroll={{ x: 600 }}
                size="small"
                columns={[
                  {
                    title: 'Компания',
                    key: 'companyName',
                    width: 200,
                    ellipsis: true,
                    render: (record: Supplier) => {
                      const data = getSupplierData(record);
                      return (
                        <div style={{ fontWeight: 500 }}>
                          {data.companyName}
                        </div>
                      );
                    },
                  },
                  {
                    title: 'ИНН',
                    key: 'inn',
                    width: 120,
                    render: (record: Supplier) => {
                      const data = getSupplierData(record);
                      return <Text code>{data.inn}</Text>;
                    },
                  },
                  {
                    title: 'Контакт',
                    key: 'contact',
                    width: 180,
                    ellipsis: true,
                    render: (record: Supplier) => {
                      const data = getSupplierData(record);
                      return (
                        <div>
                          <div style={{ fontWeight: 500 }}>{data.fullName}</div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {data.phone}
                          </Text>
                        </div>
                      );
                    },
                  },
                  {
                    title: 'Email',
                    key: 'email',
                    width: 200,
                    ellipsis: true,
                    render: (record: Supplier) => {
                      const data = getSupplierData(record);
                      return <Text type="secondary">{data.email}</Text>;
                    },
                  },
                  {
                    title: 'Статус',
                    key: 'status',
                    width: 120,
                    render: (record: Supplier) => (
                      record.isApproved ? (
                        <Tag color="green">Проверен</Tag>
                      ) : (
                        <Tag color="orange">На проверке</Tag>
                      )
                    ),
                  },
                  {
                    title: 'Действия',
                    key: 'actions',
                    width: 200,
                    render: (record: Supplier) => {
                      const alreadyRequested = myRequests.some(r => r.supplierId === record.id);
                      
                      return (
                        <Space direction="vertical" size="small">
                          <Button
                            icon={<EyeOutlined />}
                            size="small"
                            onClick={() => handleViewSupplier(record)}
                            block
                          >
                            Детали
                          </Button>
                          <Button
                            type="primary"
                            size="small"
                            onClick={() => handleSendRequest(record.id)}
                            disabled={alreadyRequested}
                            block
                          >
                            {alreadyRequested ? 'Заявка отправлена' : 'Отправить заявку'}
                          </Button>
                        </Space>
                      );
                    },
                  },
                ]}
              />
            </Card>
          )}

          {searchResults.length === 0 && !searching && (
            <Card 
              size="small" 
              style={{ textAlign: 'center', color: '#999' }}
              bodyStyle={{ padding: '40px 16px' }}
            >
              <SearchOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
              <div>Введите критерии поиска и нажмите "Найти поставщиков"</div>
            </Card>
          )}
        </div>
      </Modal>
    </>
  );
};

export default MySuppliers;

